import { memo, useContext, useState } from "react";

// MUI
import { Box, Chip, CircularProgress } from "@mui/material";

// Icons
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";

// Components
import { TimeField } from "../../input/TimeField";
import { Context } from "./Context";
import { useTranslation } from "react-i18next";
import { deletingPunch } from "../../../utils/Timecard";
import { GlobalContext } from "../../../GlobalContext";
import { trackGaClick } from "../../../utils/analytics";

const EditPunchCell = memo(({ params }) => {
  const [value, setValue] = useState("");
  const [error, setError] = useState(false);

  const { apiRef } = useContext(Context);

  const updateRow = (value) => {
    setValue(value);

    if (error || value === "") {
      // console.log("entre");

      params.row.new_punch = undefined;
      apiRef.current.updateRows([params?.row]);
      return;
    }

    const new_punch = {
      effective_datetime: `${params?.row?.date} ${value}:00`,
      identity_number: params?.row?.person_number,
      manager_name: JSON.parse(sessionStorage.getItem("account")).username,
      punch_type: params.field === "punch_in" ? "in" : "out",
      ga_type: "hour",
    };

    params.row.new_punch = new_punch;
    apiRef.current.updateRows([params?.row]);
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          placeItems: "center",
          alignItems: "center",
          alignContent: "center",
          gap: "10px",
          p: 1.5,
        }}
      >
        <TimeField
          value={value}
          setValue={updateRow}
          error={error}
          setError={setError}
        />
      </Box>
    </>
  );
});

const PunchChip = memo(
  ({
    punch,
    params,
    setAddOpenPaycode,
    setOpenConfirmDeletePunch,
    setAction,
    setCell,
    setPunch,
    setRow,
  }) => {
    const { t } = useTranslation("timecard.table");

    const { apiRef, reloading } = useContext(Context);

    const { handlerAlert } = useContext(GlobalContext);

    const [loading, setLoading] = useState(false);

    const background = (punch, theme) => {
      if ((punch.exception || punch.missing) && punch.hover) {
        return theme.palette.error.dark;
      } else if ((punch.exception || punch.missing) && !punch.hover) {
        return theme.palette.error.main;
      } else {
        return theme.palette.success.main;
      }
    };

    const props = punch.exception
      ? {
          onClick: () => {
            // console.log("row", params.row);
            setAddOpenPaycode(true);
            setAction("exception");
            setCell({
              column: undefined,
              row: params?.row?.date,
              employee_id: params?.row?.person_number,
            });
          },
          onDelete: punch.missing
            ? undefined
            : () => {
                if (loading) {
                  console.log("loading...");
                } else {
                  trackGaClick({
                    id: "timecard.delete_punch",
                    gaAction: "delete_punch",
                    gaType: params.field === "punch_in" ? "in" : "out",
                    extra: {
                      personNumber: params.row.person_number,
                      date: punch.date,
                      time: punch.time,
                    },
                  });

                  if (
                    JSON.parse(sessionStorage.getItem("privileges")).timecard
                      .confirmation_delete_punch
                  ) {
                    setOpenConfirmDeletePunch(true);
                    setPunch(punch);
                    setRow(params.row);
                  } else {
                    deletingPunch(
                      setLoading,
                      punch.date,
                      `${punch.time}:00`,
                      params.row.person_number,
                      reloading,
                      handlerAlert,
                    );
                  }
                }
              },
        }
      : {
          onDelete: () => {
            if (loading) {
              console.log("loading...");
            } else {
              trackGaClick({
                id: "timecard.delete_punch",
                gaAction: "delete_punch",
                gaType: params.field === "punch_in" ? "in" : "out",
                extra: {
                  personNumber: params.row.person_number,
                  date: punch.date,
                  time: punch.time,
                },
              });

              if (
                JSON.parse(sessionStorage.getItem("privileges")).timecard
                  .confirmation_delete_punch
              ) {
                setOpenConfirmDeletePunch(true);
                setPunch(punch);
                setRow(params.row);
              } else {
                deletingPunch(
                  setLoading,
                  punch.date,
                  `${punch.time}:00`,
                  params.row.person_number,
                  reloading,
                  handlerAlert,
                );
              }
            }
          },
        };

    return (
      <>
        {punch.missing ? (
          <Chip
            {...props}
            disabled={params?.row?.open_period.open === false}
            color="error"
            label={t("table.cells.OMITTED_PUNCH")}
            onMouseOver={() => {
              if (params.field === "punch_in") {
                params.row.exceptions.find(
                  (e) =>
                    e.total_span_index === punch.total_span_index &&
                    e.in_punch_flag === true,
                ).hover = true;
                apiRef.current.updateRows([params?.row]);
              } else {
                params.row.exceptions.find(
                  (e) =>
                    e.total_span_index === punch.total_span_index &&
                    e.in_punch_flag === false,
                ).hover = true;
                apiRef.current.updateRows([params?.row]);
              }
            }}
            onMouseOut={() => {
              if (params.field === "punch_in") {
                params.row.exceptions.find(
                  (e) =>
                    e.total_span_index === punch.total_span_index &&
                    e.in_punch_flag === true,
                ).hover = false;
                apiRef.current.updateRows([params?.row]);
              } else {
                params.row.exceptions.find(
                  (e) =>
                    e.total_span_index === punch.total_span_index &&
                    e.in_punch_flag === false,
                ).hover = false;
                apiRef.current.updateRows([params?.row]);
              }
            }}
            sx={{
              width: "160px",
              background: (theme) => background(punch, theme),
            }}
          />
        ) : (
          <Chip
            {...props}
            disabled={params?.row?.open_period.open === false}
            color={punch.exception ? "error" : "success"}
            icon={
              punch.exception ? (
                <WarningIcon
                  sx={{ cursor: "pointer", p: 0.25 }}
                  onClick={() => {
                    console.log("warning");
                  }}
                />
              ) : (
                <AssignmentIndIcon sx={{ p: 0.25 }} />
              )
            }
            label={`${punch.time}`}
            onMouseOver={() => {
              if (punch.exception) {
                if (params.field === "punch_in") {
                  params.row.exceptions.find(
                    (e) =>
                      e.total_span_index === punch.total_span_index &&
                      e.in_punch_flag === true,
                  ).hover = true;
                  apiRef.current.updateRows([params?.row]);
                } else {
                  params.row.exceptions.find(
                    (e) =>
                      e.total_span_index === punch.total_span_index &&
                      e.in_punch_flag === false,
                  ).hover = true;
                  apiRef.current.updateRows([params?.row]);
                }
              }
            }}
            onMouseOut={() => {
              if (punch.exception) {
                if (params.field === "punch_in") {
                  params.row.exceptions.find(
                    (e) =>
                      e.total_span_index === punch.total_span_index &&
                      e.in_punch_flag === true,
                  ).hover = false;
                  apiRef.current.updateRows([params?.row]);
                } else {
                  params.row.exceptions.find(
                    (e) =>
                      e.total_span_index === punch.total_span_index &&
                      e.in_punch_flag === false,
                  ).hover = false;
                  apiRef.current.updateRows([params?.row]);
                }
              }
            }}
            deleteIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              width: "160px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              "& .MuiChip-icon": {
                marginLeft: 1,
              },
              "& .MuiChip-label": {
                flexGrow: 1,
                textAlign: "center",
              },
              background: (theme) => background(punch, theme),
            }}
          />
        )}
      </>
    );
  },
);

const PunchCell = memo(
  ({
    params,
    setAddOpenPaycode,
    setOpenConfirmDeletePunch,
    setAction,
    setCell,
    setPunch,
    setRow,
  }) => {
    const regex = /auto-generated-row-null/;

    return (
      <>
        {regex.test(params?.id) ? (
          <></>
        ) : (
          <Box
            sx={{
              width: "100%",
              display: "grid",
              placeItems: "center",
              alignItems: "center",
              alignContent: "center",
              gap: "10px",
              p: 1.5,
            }}
          >
            {params?.value
              ?.sort((a, b) => a.total_span_index - b.total_span_index)
              .map((punch, index) => (
                <PunchChip
                  key={`${params?.field}-${params?.id}-${punch.total_span_index}-${index}`}
                  punch={punch}
                  params={params}
                  setAddOpenPaycode={setAddOpenPaycode}
                  setOpenConfirmDeletePunch={setOpenConfirmDeletePunch}
                  setAction={setAction}
                  setCell={setCell}
                  setPunch={setPunch}
                  setRow={setRow}
                />
              ))}
          </Box>
        )}
      </>
    );
  },
);

export { PunchCell, EditPunchCell };
